import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, mediaUrl, isHostedVideo } from "@/lib/api";
import { aisleMeta } from "@/lib/food";
import { ChevronLeft, ChevronRight, Timer, Check, Play, Pause, Video } from "lucide-react";

function useCountdown() {
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (running && seconds > 0) ref.current = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(ref.current);
  }, [running, seconds]);
  useEffect(() => { if (seconds === 0) setRunning(false); }, [seconds]);
  return { seconds, setSeconds, running, setRunning };
}

export default function CookMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [step, setStep] = useState(0);
  const [ticks, setTicks] = useState(() => new Set());
  const timer = useCountdown();

  useEffect(() => { api.get(`/recipes/${id}`).then((r) => setRecipe(r.data)).catch(() => navigate("/")); }, [id, navigate]);
  useEffect(() => { setTicks(new Set()); timer.setRunning(false); timer.setSeconds(0); }, [step]); // eslint-disable-line

  if (!recipe) return <div className="min-h-screen bg-nerd-bg flex items-center justify-center text-nerd-light/40">Loading…</div>;

  const steps = recipe.steps || [];
  const current = steps[step] || {};
  const isLast = step === steps.length - 1;
  const aisleFor = (name) => {
    const found = (recipe.ingredients || []).find((i) => i.name.toLowerCase() === (name || "").toLowerCase());
    return aisleMeta(found?.aisle || "Other");
  };

  const startStepTimer = () => { timer.setSeconds((current.duration_min || 0) * 60); timer.setRunning(true); };
  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const timerVal = timer.seconds || (current.duration_min || 0) * 60;
  const next = () => { if (!isLast) setStep((s) => s + 1); else navigate(`/recipe/${id}`); };
  const toggleTick = (i) => setTicks((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });

  return (
    <div className="min-h-screen bg-nerd-bg text-nerd-light flex flex-col">
      {/* Header */}
      <div className="shrink-0 border-b border-black/8">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between gap-4 px-4 sm:px-6 lg:px-12 h-16">
          <button data-testid="cook-close-btn" onClick={() => navigate(`/recipe/${id}`)}
            className="inline-flex items-center gap-2 text-sm font-medium text-nerd-light/60 hover:text-nerd-light transition-colors">
            <ChevronLeft className="w-4 h-4" /> Exit
          </button>
          <p className="font-display font-bold text-base sm:text-lg truncate px-2">{recipe.title}</p>
          <div className="shrink-0 flex items-center gap-2">
            {current.duration_min > 0 && (
              <div className="inline-flex items-center gap-2 bg-black/5 rounded-full pl-3 pr-1 py-1">
                <Timer className="w-4 h-4 text-nerd-light/70" strokeWidth={2} />
                <span className="font-mono-x text-sm font-semibold text-nerd-light tabular-nums" data-testid="cook-timer">{mmss(timerVal)}</span>
                {!timer.running ? (
                  <button data-testid="timer-start-btn" onClick={startStepTimer} className="w-8 h-8 rounded-full bg-nerd-light text-white flex items-center justify-center hover:opacity-90 transition-opacity">
                    <Play className="w-3.5 h-3.5 ml-0.5 fill-current" />
                  </button>
                ) : (
                  <button data-testid="timer-pause-btn" onClick={() => timer.setRunning(false)} className="w-8 h-8 rounded-full bg-nerd-light text-white flex items-center justify-center hover:opacity-90 transition-opacity">
                    <Pause className="w-3.5 h-3.5 fill-current" />
                  </button>
                )}
              </div>
            )}
            <span data-testid="cook-step-tag" className="text-xs font-mono-x uppercase tracking-widest bg-black/5 text-nerd-light/70 px-3 py-1.5 rounded-full">
              Step {step + 1}/{steps.length}
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-5 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* LEFT — media */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="relative w-full aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5] rounded-3xl overflow-hidden bg-nerd-card border border-black/8" data-testid="step-video">
              {isHostedVideo(recipe.video_url) ? (
                <video autoPlay loop muted playsInline controls className="w-full h-full object-cover bg-black" src={mediaUrl(recipe.video_url)} />
              ) : (
                <>
                  {recipe.thumbnail && <img src={mediaUrl(recipe.thumbnail)} alt="" className="w-full h-full object-cover opacity-30" />}
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-nerd-light/40">
                    <div className="w-14 h-14 rounded-full bg-black/10 border border-black/15 flex items-center justify-center">
                      <Video className="w-6 h-6" />
                    </div>
                    <p className="text-xs font-mono-x uppercase tracking-widest">Step clip</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* RIGHT — controls */}
          <div className="lg:col-span-7 min-w-0" key={step}>
            <div className="animate-fade-up">
              <p className="text-xs font-mono-x uppercase tracking-[0.2em] text-nerd-light/40 mb-3">Step {String(step + 1).padStart(2, "0")}</p>
              <h1 data-testid="cook-step-instruction" className="font-display text-base sm:text-2xl lg:text-3xl font-extrabold tracking-tight leading-snug mb-6">
                {current.instruction}
              </h1>

              {(current.ingredients_used || []).length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-mono-x uppercase tracking-[0.2em] text-nerd-light/40 mb-3">For this step</p>
                  <div className="flex lg:grid lg:grid-cols-2 gap-2.5 overflow-x-auto no-scrollbar pb-1" data-testid="cook-step-ingredients">
                    {current.ingredients_used.map((u, i) => {
                      const meta = aisleFor(u.name);
                      const Icon = meta.icon;
                      const checked = ticks.has(i);
                      return (
                        <button key={i} data-testid={`cook-ingredient-${i}`} onClick={() => toggleTick(i)}
                          className={`shrink-0 w-[200px] lg:w-auto flex items-center gap-3 p-3 rounded-2xl border text-left transition-colors ${checked ? "bg-nerd-lift border-black/5" : "bg-nerd-card border-black/8 hover:border-black/15"}`}>
                          <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${meta.tint}`}><Icon className="w-5 h-5" /></div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-sm truncate ${checked ? "line-through text-nerd-light/40" : ""}`}>{u.name}</p>
                            <p className="text-xs text-nerd-light/50">{u.quantity} {u.unit}</p>
                          </div>
                          <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center transition-colors ${checked ? "bg-nerd-light border-nerd-light text-white" : "border-black/25 text-transparent"}`}>
                            <Check className="w-3.5 h-3.5" strokeWidth={3} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Nav — mobile: two buttons side by side at the bottom */}
              <div className="flex lg:hidden items-center gap-3">
                <button data-testid="cook-prev-btn-m" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                  className="flex-1 h-14 rounded-full bg-nerd-card border border-black/8 flex items-center justify-center gap-2 font-semibold disabled:opacity-40 hover:bg-black/5 transition-colors">
                  <ChevronLeft className="w-5 h-5" /> Back
                </button>
                <button data-testid={isLast ? "cook-finish-btn-m" : "cook-next-btn-m"} onClick={next}
                  className="flex-1 h-14 rounded-full bg-nerd-light text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
                  {isLast ? (<><Check className="w-5 h-5" /> Finish</>) : (<>Next <ChevronRight className="w-5 h-5" /></>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Nav — desktop: simple icon buttons pinned to the screen edges, vertically centered */}
      <button data-testid="cook-prev-btn" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
        className="hidden lg:flex fixed left-5 top-1/2 -translate-y-1/2 z-40 w-14 h-14 rounded-full bg-nerd-card border border-black/10 items-center justify-center text-nerd-light shadow-sm hover:bg-black/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title="Previous step">
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button data-testid={isLast ? "cook-finish-btn" : "cook-next-btn"} onClick={next}
        className="hidden lg:flex fixed right-5 top-1/2 -translate-y-1/2 z-40 w-14 h-14 rounded-full bg-nerd-light text-white items-center justify-center shadow-sm hover:opacity-90 transition-opacity"
        title={isLast ? "Finish" : "Next step"}>
        {isLast ? <Check className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
      </button>
    </div>
  );
}
